import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import startCase from 'lodash/startCase'
import React, { useEffect, useState } from 'react'
import { Tag, Button, Tooltip, Flex, Typography } from 'antd'
import { EditOutlined, EyeOutlined } from '@ant-design/icons'

import RateSetTabs from './RateSetTabs'
import DataTable from '../DataTable/DataTable'
import { ColumnsModel } from '../DataTable/DataTable'
import RateSetStatus from './components/RateSetStatus'
import Link from 'next/link'
import store, { useStoreActions, useStoreState } from '../../store'
import { RateSet } from '@libs/types/generated/rates-research-api'
import { capitalizeFirstLetter } from '@utils/string'
import { rateSetStatuses } from './Form/types/ratesets'
import {
  getLastTouchedVertical,
  saveLastTouchedVertical
} from '@utils/verticals'

const RateSetList = () => {
  const router = useRouter()

  const rateSets = useStoreState(store => store.rates.rateSets)
  const markets = useStoreState(store => store.markets.markets)
  const products = useStoreState(store => store.products.products)
  const users = useStoreState(store => store.users.users)
  const advertisers = useStoreState(store => store.advertisers.advertisers)

  const [rateSetsPrepared, setRateSetsPrepared] = useState([])
  const [marketsMap, setMarketsMap] = useState({})
  const [productsMap, setProductsMap] = useState({})
  const [usersMap, setUsersMap] = useState({})

  const isStaff = useStoreActions(actions => actions.auth).userHasRole('staff')

  const [activeTab, setActiveTab] = useState('')

  const ready =
    rateSetsPrepared.length > 0 && markets.length > 0 && products.length > 0

  useEffect(() => {
    store.getActions().rates.fetchRateSets()
    store.getActions().products.fetchProducts()
    store.getActions().markets.fetchMarkets()
    store.getActions().advertisers.fetchAdvertisers()
    store.getActions().users.fetchUsers()
  }, [])

  // Convert markets to key value pairs
  useEffect(() => {
    const mapping = markets.reduce((acc, market) => {
      acc[market.id] = market
      return acc
    }, {})
    setMarketsMap(mapping)
  }, [markets])

  // Convert products to key value pairs
  useEffect(() => {
    const mapping = products.reduce((acc, product) => {
      acc[product.id] = product.name
      return acc
    }, {})
    setProductsMap(mapping)
  }, [products])

  // Convert users to key value pairs
  useEffect(() => {
    const mapping = users.reduce((acc, user) => {
      acc[user.user_id] = user.name
      return acc
    }, {})
    setUsersMap(mapping)
  }, [users])

  // Set the initial active tab when rateSets is updated
  useEffect(() => {
    if (
      Object.keys(marketsMap).length === 0 ||
      Object.keys(productsMap).length === 0 ||
      rateSets.length === 0
    ) {
      return
    }

    setActiveTab(getLastTouchedVertical(true) ?? 'auto')

    setRateSetsPrepared(
      rateSets.map(rateSet => {
        const markets = (rateSet.markets || []).reduce((acc, id) => {
          const market = marketsMap[id]
          if (market) {
            acc.push(
              market.name +
                ' - ' +
                capitalizeFirstLetter(market.market_type.name)
            )
          } else {
            //console.error(`Market with id ${id} not found`)
          }
          return acc
        }, [])

        const products = rateSet.products.reduce((acc, id) => {
          const product = productsMap[id]
          if (product) {
            acc.push(product)
          } else {
            //console.error(`Product with id ${id} not found`)
          }
          return acc
        }, [])

        const advertiser = advertisers.find(
          advertiser => advertiser.id == rateSet.advertiser_id.toString()
        )

        return {
          ...rateSet,
          status_label: rateSetStatuses[rateSet.status],
          assigned_user: usersMap[rateSet.assigned_user_id] || null,
          markets,
          products,
          advertiser: advertiser
            ? `${advertiser.name ?? advertiser.dba} (${advertiser.id}) ${
                advertiser.attributes['institution_type']
                  ? ' - ' + advertiser.attributes['institution_type']
                  : ''
              }`
            : `Unknown - inactive/not-onboarded (${rateSet.advertiser_id})`
        }
      })
    )
  }, [rateSets, marketsMap, productsMap, usersMap])

  const handleTabChange = key => {
    setActiveTab(key)
    saveLastTouchedVertical(key)
  }

  const filteredRateSets = rateSetsPrepared.filter(rateSet => {
    if (activeTab === 'all_loans') {
      return ['auto', 'home-equity', 'personal-loans'].includes(
        rateSet.vertical
      )
    } else {
      return rateSet.vertical === activeTab
    }
  })

  const columns: ColumnsModel[] = [
    {
      title: 'Advertiser',
      dataIndex: 'advertiser',
      sortable: true,
      filterable: true
    },
    {
      title: 'Name',
      dataIndex: 'display_name',
      filterable: true
    },
    {
      title: 'Group',
      dataIndex: ['attributes', 'group'],
      filterable: true
    },
    {
      title: 'Status',
      dataIndex: 'status_label',
      sortable: true,
      filterable: true,
      render: statusLabel => {
        return <RateSetStatus status={statusLabel} />
      }
    },
    {
      title: 'Markets',
      dataIndex: 'markets',
      sortable: false,
      filterable: true,
      filterKeys: ['name', 'stateCode', 'stateName'],
      render: (marketList: [], row: RateSet) => {
        marketList = marketList || []
        const collapseLength = 3
        const markets =
          marketList.length > collapseLength
            ? marketList.slice(0, collapseLength)
            : marketList

        return (
          <>
            {markets.map(marketName => (
              <Tag key={marketName}>{marketName}</Tag>
            ))}
            {marketList.length > collapseLength && (
              <Button
                type="link"
                size="small"
                onClick={() => router.push(`/rate-sets/${row.id}/edit`)}
              >
                +{marketList.length - 3} more
              </Button>
            )}
          </>
        )
      }
    },
    {
      title: 'Products',
      dataIndex: 'products',
      sortable: false,
      filterable: true,
      filterKeys: ['name'],
      render: (productList: [], row: RateSet) => {
        const collapseLength = 3
        productList = productList || []
        const products =
          productList.length > collapseLength
            ? productList.slice(0, collapseLength)
            : productList

        return (
          <>
            {products.map(productName => (
              <Tag key={productName}>{productName}</Tag>
            ))}
            {productList.length > collapseLength && (
              <Button
                type="link"
                size="small"
                onClick={() => router.push(`/rate-sets/${row.id}/edit`)}
              >
                +{productList.length - 3} more
              </Button>
            )}
          </>
        )
      }
    },
    {
      title: 'Assignee',
      dataIndex: 'assigned_user',
      sortable: true,
      filterable: true
    },
    {
      title: 'Expires',
      dataIndex: 'expiration_date',
      isDate: true,
      sortable: true,
      filterable: false,
      render: isoString => {
        const date = DateTime.fromISO(isoString)
        return date.toFormat('M/d/yy')
      }
    }
  ]

  return (
    ready && (
      <>
        <RateSetTabs activeTab={activeTab} onTabChange={handleTabChange} />
        <Flex justify="space-between" style={{ margin: '16px 0' }}>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            <span
              style={{
                backgroundColor: '#e6f4ff',
                padding: '0 6px',
                color: '#1777ff',
                marginRight: '10px',
                borderRadius: '4px'
              }}
            >
              {startCase(activeTab)}
            </span>
            Rate Sets
          </Typography.Title>
          {isStaff && (
            <Link key="1" href="/rate-sets/create">
              <Button
                type="primary"
                size="large"
                data-testid="add_rate_set_link"
              >
                Add Rate Set
              </Button>
            </Link>
          )}
        </Flex>
        <DataTable
          columns={columns}
          dataSource={filteredRateSets}
          data-testid="rate_set_list"
          rowActions={[
            row => (
              <Tooltip title="Edit Rates">
                <Link href={`/rate-sets/${row.id}/edit`} target="_blank">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    data-testid="row_action_next"
                  />
                </Link>
              </Tooltip>
            ),
            row =>
              row.status !== 'NEED_ENTRY' &&
              isStaff && (
                <Tooltip title="Review Rates Entry">
                  <Link href={`/rate-sets/${row.id}/review`} target="_blank">
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      data-testid="row_action_review"
                    />
                  </Link>
                </Tooltip>
              )
          ]}
        />
      </>
    )
  )
}

export default RateSetList
